/**
 * Shared WebGL utility functions for 3D texture operations
 * used by GPU-accelerated SDF renderers (anisotropic margin,
 * Gaussian smoothing, etc.).
 */

import { gl } from '../../../Engine';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';

/**
 * Create an empty R8 3D texture with LINEAR filtering
 * and CLAMP_TO_EDGE wrapping.
 */
export function createSdfTexture3D(
    nx: number,
    ny: number,
    nz: number
): WebGLTexture | null {
    const texture = gl.createTexture();
    if (!texture) {
        return null;
    }

    gl.bindTexture(gl.TEXTURE_3D, texture);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
    gl.texImage3D(
        gl.TEXTURE_3D,
        0,
        gl.R8,
        nx,
        ny,
        nz,
        0,
        gl.RED,
        gl.UNSIGNED_BYTE,
        null
    );
    gl.bindTexture(gl.TEXTURE_3D, null);

    return texture;
}

/**
 * Create an empty R32F 3D texture with NEAREST filtering and
 * CLAMP_TO_EDGE wrapping. Rendering to R32F requires the
 * EXT_color_buffer_float extension to be enabled by the caller.
 *
 * NEAREST is used to avoid the OES_texture_float_linear
 * requirement; callers that need linear sampling should
 * enable that extension themselves and request LINEAR.
 */
export function createR32FTexture3D(
    nx: number,
    ny: number,
    nz: number
): WebGLTexture | null {
    const texture = gl.createTexture();
    if (!texture) {
        return null;
    }

    gl.bindTexture(gl.TEXTURE_3D, texture);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
    gl.texImage3D(
        gl.TEXTURE_3D,
        0,
        gl.R32F,
        nx,
        ny,
        nz,
        0,
        gl.RED,
        gl.FLOAT,
        null
    );
    const allocationError = gl.getError();
    gl.bindTexture(gl.TEXTURE_3D, null);

    if (allocationError !== gl.NO_ERROR) {
        gl.deleteTexture(texture);
        return null;
    }

    return texture;
}

/**
 * Per-channel bit depths and numeric class of a colour
 * attachment, as reported by the framebuffer attachment queries.
 * Used to compare two textures' formats without needing the
 * (unqueryable in WebGL2) internal format enum.
 */
interface ColorFormat {
    red: number;
    green: number;
    blue: number;
    alpha: number;
    componentType: number;
}

/**
 * Read the colour format of the texture currently attached to
 * COLOR_ATTACHMENT0 of the bound framebuffer.
 */
function readColorAttachmentFormat(): ColorFormat {
    const param = (pname: number): number =>
        gl.getFramebufferAttachmentParameter(
            gl.FRAMEBUFFER,
            gl.COLOR_ATTACHMENT0,
            pname
        ) as number;
    return {
        red: param(gl.FRAMEBUFFER_ATTACHMENT_RED_SIZE),
        green: param(gl.FRAMEBUFFER_ATTACHMENT_GREEN_SIZE),
        blue: param(gl.FRAMEBUFFER_ATTACHMENT_BLUE_SIZE),
        alpha: param(gl.FRAMEBUFFER_ATTACHMENT_ALPHA_SIZE),
        componentType: param(gl.FRAMEBUFFER_ATTACHMENT_COMPONENT_TYPE),
    };
}

function describeColorFormat(f: ColorFormat): string {
    return (
        `R${f.red}G${f.green}B${f.blue}A${f.alpha} ` +
        `componentType=0x${f.componentType.toString(16)}`
    );
}

/**
 * Assert the bound framebuffer is complete, i.e. its colour
 * attachment is renderable at its format. Throws with `context`
 * prefix otherwise.
 *
 * Cheap enough to call once per render pass; a pass that attaches
 * the same texture layer by layer only needs one check, since
 * completeness cannot change between layers of one texture.
 */
export function assertFramebufferComplete(context: string): void {
    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
        throw new Error(
            `${context} is not a complete colour-renderable ` +
                `attachment (status 0x${status.toString(16)})`
        );
    }
}

/**
 * Attach `texture` layer 0 to COLOR_ATTACHMENT0, assert the
 * framebuffer is complete (i.e. the format is colour-renderable),
 * and return its colour format. Throws with `context` prefix
 * otherwise.
 */
function attachAndReadFormat(
    texture: WebGLTexture,
    context: string
): ColorFormat {
    gl.framebufferTextureLayer(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        texture,
        0,
        0
    );
    assertFramebufferComplete(context);
    return readColorAttachmentFormat();
}

/**
 * Assert that `format` is single-channel 8-bit unsigned-normalised
 * (R8): red is 8 bits, the other channels absent, and the numeric
 * class normalised. Throws with `context` prefix otherwise.
 */
function assertR8Format(format: ColorFormat, context: string): void {
    const isR8 =
        format.red === 8 &&
        format.green === 0 &&
        format.blue === 0 &&
        format.alpha === 0 &&
        format.componentType === gl.UNSIGNED_NORMALIZED;
    if (!isR8) {
        throw new Error(
            `${context}: expected an R8 texture but got ` +
                `${describeColorFormat(format)}`
        );
    }
}

/**
 * GPU-side copy of a 3D colour texture into another of the same
 * format, layer by layer via gl.copyTexSubImage3D. No CPU round
 * trip. Format-agnostic: works for any colour-renderable format
 * (R8, RGBA8, ...).
 *
 * Both textures must already be allocated at least `nx`×`ny`×`nz`
 * and share the same colour format. The format/renderability of
 * src and dst is asserted up front (once per call, not per layer)
 * so an incompatible pair fails fast with an actionable error
 * rather than silently no-op'ing (incomplete fbo) or truncating
 * channels (mismatched formats), which copyTexSubImage3D would
 * otherwise do.
 */
export function copyTexture3D(
    src: WebGLTexture,
    dst: WebGLTexture,
    nx: number,
    ny: number,
    nz: number
): void {
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    try {
        const srcFormat = attachAndReadFormat(src, 'copyTexture3D: source');
        const dstFormat = attachAndReadFormat(
            dst,
            'copyTexture3D: destination'
        );
        if (
            srcFormat.red !== dstFormat.red ||
            srcFormat.green !== dstFormat.green ||
            srcFormat.blue !== dstFormat.blue ||
            srcFormat.alpha !== dstFormat.alpha ||
            srcFormat.componentType !== dstFormat.componentType
        ) {
            throw new Error(
                `copyTexture3D: src/dst colour formats differ ` +
                    `(src ${describeColorFormat(srcFormat)}, ` +
                    `dst ${describeColorFormat(dstFormat)})`
            );
        }

        gl.bindTexture(gl.TEXTURE_3D, dst);
        for (let z = 0; z < nz; z++) {
            gl.framebufferTextureLayer(
                gl.FRAMEBUFFER,
                gl.COLOR_ATTACHMENT0,
                src,
                0,
                z
            );
            gl.copyTexSubImage3D(gl.TEXTURE_3D, 0, 0, 0, z, 0, 0, nx, ny);
        }
        gl.bindTexture(gl.TEXTURE_3D, null);
    } finally {
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }
}

/**
 * Read an R8 3D texture back to per-slice Uint8Array[] via
 * gl.RED + UNSIGNED_BYTE (1 byte/voxel). Synchronous so that no
 * yield can happen with the framebuffer still bound.
 *
 * The one-byte-per-voxel Uint8Array[] result is only meaningful
 * for a single-channel 8-bit texture, so the source format is
 * asserted to be R8 up front: a wrong-format source fails loudly
 * instead of being silently mis-read.
 */
export function readR8Texture3D(
    texture: WebGLTexture,
    nx: number,
    ny: number,
    nz: number,
    signal?: AbortSignal
): Uint8Array[] {
    const result: Uint8Array[] = new Array(nz);
    const fb = gl.createFramebuffer();
    const sliceSize = nx * ny;

    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    try {
        const format = attachAndReadFormat(texture, 'readR8Texture3D: source');
        assertR8Format(format, 'readR8Texture3D');

        // R8 rows may not be a multiple of 4 bytes; disable row padding.
        gl.pixelStorei(gl.PACK_ALIGNMENT, 1);

        for (let z = 0; z < nz; z++) {
            if (signal?.aborted) {
                throw new DOMException('Operation aborted', 'AbortError');
            }

            gl.framebufferTextureLayer(
                gl.FRAMEBUFFER,
                gl.COLOR_ATTACHMENT0,
                texture,
                0,
                z
            );
            const slice = new Uint8Array(sliceSize);
            gl.readPixels(0, 0, nx, ny, gl.RED, gl.UNSIGNED_BYTE, slice);
            result[z] = slice;
        }
    } finally {
        gl.pixelStorei(gl.PACK_ALIGNMENT, 4);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    return result;
}

/** Upload per-slice Uint8Array data into a 3D R8 texture. */
export function uploadSlicesToTexture3D(
    target: WebGLTexture,
    slices: Uint8Array[],
    nx: number,
    ny: number
): void {
    gl.bindTexture(gl.TEXTURE_3D, target);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    for (let z = 0; z < slices.length; z++) {
        gl.texSubImage3D(
            gl.TEXTURE_3D,
            0,
            0,
            0,
            z,
            nx,
            ny,
            1,
            gl.RED,
            gl.UNSIGNED_BYTE,
            slices[z]
        );
    }
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.bindTexture(gl.TEXTURE_3D, null);
}

/**
 * Read back an R8 FBO and write the pixels into a 3D
 * texture along a non-Z axis (coronal Y=1 or sagittal X=0).
 *
 * @param fbo       The FBO whose colour attachment holds the
 *                  rendered 2D SDF slice.
 * @param target    The destination 3D texture.
 * @param sliceAxis 0 (sagittal) or 1 (coronal).
 * @param sliceIndex Voxel index along the slice axis.
 * @param width     Planar width of the FBO (pixels).
 * @param height    Planar height of the FBO (pixels).
 */
export function copyFboToTexture3DSlice(
    fbo: FrameBufferObject,
    target: WebGLTexture,
    sliceAxis: 0 | 1,
    sliceIndex: number,
    width: number,
    height: number
): Uint8Array {
    // Read R8 FBO directly via gl.RED (1 byte/pixel),
    // same pattern as SignedDistanceField.sliceToArrayInternal.
    fbo.bind();
    const pixelCount = width * height;
    const pixels = new Uint8Array(pixelCount);
    gl.pixelStorei(gl.PACK_ALIGNMENT, 1);
    gl.readPixels(0, 0, width, height, gl.RED, gl.UNSIGNED_BYTE, pixels);
    gl.pixelStorei(gl.PACK_ALIGNMENT, 4);
    fbo.unbind();

    gl.bindTexture(gl.TEXTURE_3D, target);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

    if (sliceAxis === 1) {
        // Coronal: slab of (width, 1, height) at y = sliceIndex.
        gl.texSubImage3D(
            gl.TEXTURE_3D,
            0,
            0,
            sliceIndex,
            0,
            width,
            1,
            height,
            gl.RED,
            gl.UNSIGNED_BYTE,
            pixels
        );
    } else {
        // Sagittal: slab of (1, width, height) at x = sliceIndex.
        gl.texSubImage3D(
            gl.TEXTURE_3D,
            0,
            sliceIndex,
            0,
            0,
            1,
            width,
            height,
            gl.RED,
            gl.UNSIGNED_BYTE,
            pixels
        );
    }

    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.bindTexture(gl.TEXTURE_3D, null);

    return pixels;
}

/**
 * Render a per-slice FBO pass over the Z slices [zMin..zMax]
 * (all slices by default) of a 3D texture. Runs
 * synchronously to avoid GL state corruption
 * from yielding while shader, VAO, and texture bindings are
 * active. Expects the caller to have already bound a quad
 * VAO (via RenderStorage.getQuadVAO().bind(0)) and started
 * the shader program.
 */
export function renderSlicePass(
    srcTex: WebGLTexture,
    dstTex: WebGLTexture,
    fbo: FrameBufferObject,
    sliceZUniform: UniformFloat,
    nz: number,
    signal?: AbortSignal,
    zMin: number = 0,
    zMax: number = nz - 1
): void {
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_3D, srcTex);

    for (let z = zMin; z <= zMax; z++) {
        if (signal?.aborted) {
            throw new DOMException('Operation aborted', 'AbortError');
        }

        sliceZUniform.loadFloat((z + 0.5) / nz);

        fbo.bind();
        fbo.createTextureLayer(dstTex, z);

        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
}
